import { motion } from "framer-motion";
import { ArrowRight, Heart } from "lucide-react";
import { IMAGES } from "@/constants/brand";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0b0d10]"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Father embracing his child at sunset"
          className="w-full h-full object-cover object-center opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0d10] via-[#0b0d10]/85 to-[#0b0d10]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d10] via-transparent to-[#0b0d10]/60" />
        <div className="absolute inset-0 grain-overlay opacity-[0.08]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 text-[#e2562a] uppercase tracking-[0.35em] text-xs font-bold mb-7"
          >
            <span className="h-px w-10 bg-[#e2562a]" />
            A movement for fathers
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display uppercase text-6xl md:text-8xl text-white leading-[0.92] tracking-tight"
          >
            Redefining
            <br />
            Modern
            <br />
            <span className="text-[#e2562a]">Fatherhood</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 text-lg md:text-xl text-white/70 leading-relaxed max-w-xl"
          >
            Equipping men with the resources, mentorship, emotional support, and
            community needed to become active leaders within their homes and
            communities.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-donate-btn"
              onClick={() => scrollTo("get-involved")}
              className="inline-flex items-center justify-center gap-2 bg-[#e2562a] hover:bg-[#c4451d] text-white px-8 py-4 font-bold uppercase tracking-wider transition-all shadow-[0_12px_32px_-10px_rgba(226,86,42,0.7)] hover:-translate-y-0.5"
            >
              <Heart className="h-5 w-5" strokeWidth={2.5} />
              Support the Mission
            </button>
            <button
              data-testid="hero-involved-btn"
              onClick={() => scrollTo("programs")}
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-white hover:bg-white/5 px-8 py-4 font-bold uppercase tracking-wider transition-all"
            >
              Explore Programs
              <ArrowRight className="h-5 w-5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-xl border-t border-white/10 pt-8"
          >
            {[
              ["Love", "& Leadership"],
              ["Mentorship", "& Healing"],
              ["Stronger", "Communities"],
            ].map(([a, b]) => (
              <div key={a} className="flex flex-col">
                <span className="font-display uppercase text-2xl md:text-3xl text-white leading-none">
                  {a}
                </span>
                <span className="text-xs uppercase tracking-wider text-white/50 mt-1">
                  {b}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
